import type { ComponentType } from 'react'
import { filterPostsByLocale, type BlogLocale } from '@saas-pages/shared/public/blog'
import type { ToolRouteKey } from '@/seo/routes'

export interface BlogFaqItem {
  q: string
  a: string
}

interface BlogFrontmatter {
  title: string
  description: string
  slug?: string
  locale: BlogLocale
  date: string
  updated?: string
  tags?: string[]
  relatedTools?: ToolRouteKey[]
  faq?: BlogFaqItem[]
  translations?: Partial<Record<BlogLocale, string>>
}

interface MdxModule {
  default: ComponentType
  frontmatter: BlogFrontmatter
}

export interface BlogPost {
  slug: string
  locale: BlogLocale
  title: string
  description: string
  date: string
  updated?: string
  tags: string[]
  relatedTools: ToolRouteKey[]
  faq: BlogFaqItem[]
  translations: Partial<Record<BlogLocale, string>>
  Component: ComponentType
}

const modules = import.meta.glob<MdxModule>('../../content/blog/*.mdx', {
  eager: true,
})

function slugFromPath(path: string): string {
  const file = path.split('/').pop() ?? path
  return file.replace(/\.mdx$/, '')
}

const POSTS: BlogPost[] = Object.entries(modules)
  .map(([path, mod]) => {
    const fm = mod.frontmatter
    if (
      !fm
      || typeof fm.title !== 'string'
      || typeof fm.description !== 'string'
      || typeof fm.date !== 'string'
      || (fm.locale !== 'ru' && fm.locale !== 'en')
    ) {
      throw new Error(
        `Blog post ${path} is missing required frontmatter (title, description, date, locale)`,
      )
    }

    return {
      slug: fm.slug ?? slugFromPath(path),
      locale: fm.locale,
      title: fm.title,
      description: fm.description,
      date: fm.date,
      updated: fm.updated,
      tags: fm.tags ?? [],
      relatedTools: fm.relatedTools ?? [],
      faq: fm.faq ?? [],
      translations: fm.translations ?? {},
      Component: mod.default,
    }
  })
  .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))

export function getAllPosts(locale?: BlogLocale): BlogPost[] {
  return locale ? filterPostsByLocale(POSTS, locale) : POSTS
}

export function getPostBySlug(slug: string): BlogPost | undefined {
  return POSTS.find((post) => post.slug === slug)
}
